/**
 * Detect trigger/action setup problems (auth, required fields) for agent detail UI.
 */

import { findBuiltInActionApp } from "../../constants/builtInActionApps";

const extractPrimitive = (value) => {
  let current = value;
  while (
    current &&
    typeof current === "object" &&
    !Array.isArray(current) &&
    Object.prototype.hasOwnProperty.call(current, "value")
  ) {
    current = current.value;
  }
  return current;
};

const isUnsetForSetup = (value) => {
  const p = extractPrimitive(value);
  if (p === undefined || p === null) return true;
  if (typeof p === "string") {
    const t = p.trim();
    if (!t) return true;
    if (t === "Let agent pick value") return true;
  }
  return false;
};

/** Mirrors AgentActionsSection / AgentWatchesSection flattening for required checks. */
export const flattenAgentFieldsForSetup = (fields = []) => {
  const base = Array.isArray(fields) ? fields : [];
  const result = [];
  base.forEach((field) => {
    if (!field || field.hidden === true || field?.schema?.hidden === true) return;
    if (field.name === "body" && field?.schema?.type === "object" && field?.schema?.properties) {
      const bodyRequired = Array.isArray(field.schema.required) ? field.schema.required : [];
      const bodyValue = field.value && typeof field.value === "object" ? field.value : {};
      Object.keys(field.schema.properties).forEach((propertyName) => {
        const propertySchema = field.schema.properties[propertyName] || {};
        if (propertySchema.hidden === true) return;
        result.push({
          __path: `body.${propertyName}`,
          name: propertyName,
          title: propertySchema.title || propertyName,
          required: bodyRequired.includes(propertyName),
          schema: propertySchema,
          value: bodyValue[propertyName] ?? "Let agent pick value",
          parentName: "body",
        });
      });
      return;
    }
    result.push({
      ...field,
      __path: field.name,
      value: field.value ?? "Let agent pick value",
      required: field.required === true || field.schema?.required === true,
    });
  });
  return result;
};

const credentialPresent = (entity) => {
  const c = entity?.credential ?? entity?.config?.credential ?? "";
  return Boolean(c && String(c).trim());
};

/**
 * Match agent `app` / trigger `source` to an allApps row (handles passportApp, slug, casing).
 */
export const findCatalogAppMeta = (catalogApps, appSlug) => {
  if (!appSlug) return null;
  const s = String(appSlug).toLowerCase().trim();
  if (Array.isArray(catalogApps) && catalogApps.length > 0) {
    for (const item of catalogApps) {
      const keys = [item.app, item.passportApp, item.slug, item.app_slug]
        .filter(Boolean)
        .map((x) => String(x).toLowerCase().trim());
      if (keys.includes(s)) return item;
    }
  }
  return findBuiltInActionApp(s);
};

/** API may send auth as boolean, "true", 1, or omit it; Slack/Gmail often omit `auth` but still use OAuth. */
const truthyAuthFlag = (value) => {
  if (value === true || value === 1) return true;
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    return s === "true" || s === "1" || s === "yes";
  }
  return false;
};

const falsyAuthFlag = (value) => {
  if (value === false || value === 0 || value === null) return true;
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    return s === "false" || s === "0" || s === "no" || s === "";
  }
  return false;
};

/** Catalog uses "Utility", "Conversion & Lookup Utilities", etc. — no credential / setup-issue nags for these. */
const isUtilityCatalogCategory = (category) => {
  const c = String(category || "").trim().toLowerCase();
  if (!c) return false;
  return /\butilities\b|\butility\b/i.test(c);
};

/**
 * Action editor: respect `auth === false` from catalog; omit-only auth uses formData/url heuristics below.
 * Phase C may draft auth onto the agent action (`needsCredential`) even when catalog still says auth:false.
 */
const actionAppExpectsCredential = (appMeta, action) => {
  if (action?.needsCredential || action?.config?.needsCredential || action?.config?.authDrafted) {
    return true;
  }
  const actionKey = String(action?.action_key || action?.config?.action || "").toLowerCase();
  const isCustomRequest =
    actionKey === "custom request" || actionKey === "customrequest" || actionKey === "workload_http";
  if (isCustomRequest && (action?.apiDocs || action?.config?.apiDocs || appMeta?.apiDocs || appMeta?.apiDocsUrl)) {
    return true;
  }
  if (!appMeta || typeof appMeta !== "object") return false;
  const slug = String(appMeta.app || "").toLowerCase();
  if (!slug || slug === "schedule") return false;
  if (slug === "workload-code" || slug === "workload-browser") return false;
  if (isUtilityCatalogCategory(appMeta.category)) return false;
  if (falsyAuthFlag(appMeta.auth) && !action?.needsCredential && !action?.config?.needsCredential) {
    return false;
  }
  if (truthyAuthFlag(appMeta.auth) || appMeta.needsCredential === true) return true;
  if (Array.isArray(appMeta.auths) && appMeta.auths.length > 0) return true;
  return Boolean(appMeta.formDataEndpoint || appMeta.url);
};

/**
 * When Redux catalog has no row (slug mismatch, fetch pending), agent actions still store the
 * Workload `.../apps/{slug}` URL from the builder — treat that like an integration that needs connect.
 */
const actionPayloadImpliesConnectableIntegration = (action) => {
  if (action?.needsCredential || action?.config?.needsCredential || action?.config?.authDrafted) {
    return true;
  }
  const slug = String(action?.app || "").toLowerCase();
  if (!slug || slug === "schedule" || slug === "workload-code" || slug === "workload-browser") return false;
  const u = String(action?.url || "").toLowerCase();
  return u.includes("/apps/");
};

/**
 * True if the user should connect an account before this tool can run reliably.
 * @param {boolean} catalogReady — when false and we have no catalog row, do not infer from URL (avoids flash before /apps/ loads). Rows from catalog are evaluated immediately.
 */
const actionRequiresConnectAccount = (action, appMeta, catalogReady) => {
  if (actionAppExpectsCredential(appMeta, action)) return true;
  if (appMeta && typeof appMeta === "object") return false;
  if (!catalogReady) return false;
  return actionPayloadImpliesConnectableIntegration(action);
};

/** Trigger modal only wraps Authorizer when appMeta?.auth is truthy; align with that + auths list. */
const triggerAppExpectsCredential = (appMeta) => {
  if (!appMeta || typeof appMeta !== "object") return false;
  if (isUtilityCatalogCategory(appMeta.category)) return false;
  if (falsyAuthFlag(appMeta.auth)) return false;
  if (truthyAuthFlag(appMeta.auth)) return true;
  return Array.isArray(appMeta.auths) && appMeta.auths.length > 0;
};

/**
 * @param {object} trigger
 * @param {object|null} appMeta — row from allApps triggers (auth, app, name, …)
 * @returns {{ tooltip: string, alertTitle: string, alertBody: string } | null}
 */
export const getTriggerSetupIssue = (trigger, appMeta) => {
  if (!trigger || trigger.enabled === false) return null;
  if (!trigger.source || !trigger.type) {
    return {
      tooltip: "Choose a trigger app and event to finish setup.",
      alertTitle: "Trigger not finished",
      alertBody:
        "Select a trigger app and event, then save. The agent cannot run until a watch is fully configured.",
    };
  }
  const lines = [];
  if (triggerAppExpectsCredential(appMeta) && !credentialPresent(trigger)) {
    lines.push("Missing authentication: this app requires a connected account.");
  }
  const flat = flattenAgentFieldsForSetup(trigger.fields || []);
  flat.forEach((f) => {
    const req = f.required === true || f.schema?.required === true;
    if (req && isUnsetForSetup(f.value)) {
      lines.push(`Required field "${f.title || f.name}" is not set.`);
    }
  });
  if (lines.length === 0) return null;
  const how =
    "Open the trigger editor, connect a credential if prompted, then fill every required field and click Save.";
  return {
    tooltip: lines.join("\n"),
    alertTitle: "Fix trigger setup",
    alertBody: `${lines.join(" ")}\n\n${how}`,
  };
};

/**
 * @param {object} action
 * @param {object|null} appMeta — row from allApps actions
 * @param {{ catalogReady?: boolean }} [options] — set catalogReady false until allApps has finished loading
 */
export const getActionSetupIssue = (action, appMeta, { catalogReady = true } = {}) => {
  if (!action) return null;
  if (!action.app || !action.action_key) {
    return {
      tooltip: "Choose an app and action for this tool.",
      alertTitle: "Tool not finished",
      alertBody: "Select an app and action, then configure fields and save.",
    };
  }
  if (appMeta && isUtilityCatalogCategory(appMeta.category)) {
    return null;
  }
  const lines = [];
  if (actionRequiresConnectAccount(action, appMeta, catalogReady) && !credentialPresent(action)) {
    lines.push("Missing authentication: connect an account for this integration.");
  }
  // Required tool fields may stay on "Let agent pick value" — the agent resolves them at run time.
  // Only auth / unfinished app+action selection are setup blockers here.
  if (lines.length === 0) return null;
  const how =
    "Open tool configuration and use “Connect credential” if you see it, then save.";
  return {
    tooltip: lines.join("\n"),
    alertTitle: "Fix tool setup",
    alertBody: `${lines.join(" ")}\n\n${how}`,
  };
};
